import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api';
import '../PlatformDashboard.css';

const STATUS_FILTERS = ['ALL', 'COMPLETED', 'SCANNING', 'PENDING', 'FAILED'];

const TIER_COLOR = {
  Critical: '#dc2626',
  High: '#ea580c',
  Moderate: '#d97706',
  Lower: '#16a34a',
};
const STATUS_COLOR = {
  COMPLETED: '#16a34a',
  SCANNING: '#0ea5e9',
  PENDING: '#6b7280',
  FAILED: '#dc2626',
};

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch (_) { return ts; }
}

function PublicSnapshots() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('ALL');

  const load = useCallback(async (statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const params = { limit: 500 };
      if (statusFilter && statusFilter !== 'ALL') params.status = statusFilter;
      const res = await axiosInstance.get('/admin/public-snapshots', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const counts = data?.statusCounts || {};
  const rows = data?.snapshots || [];

  return (
    <div className="pd-page">
      <section className="pd-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 className="pd-section-title" style={{ margin: 0 }}>
            <i className="fas fa-bullseye" style={{ marginRight: 8 }} /> Public Attack-Surface Snapshots
          </h2>
          <button className="pd-action-btn" onClick={() => load(status)} disabled={loading}>
            <i className="fas fa-sync" /> Refresh
          </button>
        </div>
        <p className="pd-empty" style={{ marginTop: 6 }}>
          Lead-gen scans from cataam.com/scan. These run under the isolated <code>SYSTEM_PUBLIC_SCANS</code> org,
          so they do not appear in any tenant&apos;s ASM history.
        </p>

        {/* status chips / filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
          {STATUS_FILTERS.map((s) => {
            const active = status === s;
            const n = s === 'ALL'
              ? Object.values(counts).reduce((a, b) => a + Number(b || 0), 0)
              : Number(counts[s] || 0);
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                style={{
                  cursor: 'pointer',
                  borderRadius: 999,
                  padding: '4px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  border: active ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                  background: active ? '#3b82f618' : '#fff',
                  color: active ? '#1d4ed8' : '#475569',
                }}
              >
                {s} <span style={{ opacity: 0.7 }}>({n})</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="pd-section">
        {loading ? (
          <div className="pd-loading"><div className="pd-spinner" /></div>
        ) : error ? (
          <div className="pd-error">Failed to load: {String(error)}</div>
        ) : rows.length === 0 ? (
          <p className="pd-empty">No public snapshots yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Lead</th>
                  <th>Status</th>
                  <th>Fragility</th>
                  <th>Percentile</th>
                  <th>C / H / M</th>
                  <th>Top categories</th>
                  <th>Requested</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.domain}</td>
                    <td>
                      <div>{r.email}</div>
                      {(r.name || r.company) && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {[r.name, r.company].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        fontWeight: 600, fontSize: 12,
                        color: STATUS_COLOR[r.status] || '#475569',
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      {r.fragility != null ? (
                        <span style={{ fontWeight: 700, color: TIER_COLOR[r.fragility_tier] || '#0f172a' }}>
                          {r.fragility}<span style={{ fontWeight: 400, color: '#94a3b8' }}>/100</span>
                          {r.fragility_tier ? ` · ${r.fragility_tier}` : ''}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{r.percentile != null ? `~${r.percentile}%` : '—'}</td>
                    <td>{r.fragility != null ? `${r.crit ?? 0} / ${r.high ?? 0} / ${r.med ?? 0}` : '—'}</td>
                    <td style={{ fontSize: 12, color: '#475569', maxWidth: 240 }}>{r.top_categories || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{fmt(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default PublicSnapshots;
